import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import SEOHead from "@/components/feature/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories, articles, type Article } from "@/mocks/articles";

const ARTICLES_PER_PAGE = 9;

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [page, setPage] = useState(1);

  const categoryArticles = useMemo(() => {
    if (!category) return [];
    return [...articles]
      .filter((a) => a.category === category.id)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [category]);

  const totalPages = Math.max(1, Math.ceil(categoryArticles.length / ARTICLES_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const displayArticles = categoryArticles.slice(
    (currentPage - 1) * ARTICLES_PER_PAGE,
    currentPage * ARTICLES_PER_PAGE
  );

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center px-4">
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">
              分类不存在
            </h1>
            <p className="text-foreground-500 text-sm mb-6">
              请检查分类地址是否正确
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 911吃瓜网`,
    description: category.description,
    url: `http://91kankan.cn/category/${category.slug}`,
    isPartOf: { "@type": "WebSite", name: "911吃瓜网", url: "http://91kankan.cn/" },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "http://91kankan.cn/" },
      { "@type": "ListItem", position: 2, name: category.name, item: `http://91kankan.cn/category/${category.slug}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 911吃瓜网 | ${category.description}`}
        description={`911吃瓜网${category.name}栏目：${category.description}。每日更新最新最热${category.name}内容，共${category.articleCount}篇爆料文章。`}
        keywords={`${category.name},911吃瓜网,吃瓜爆料,网红翻车,明星黑料,社会热点`}
        canonicalUrl={`http://91kankan.cn/category/${category.slug}`}
        ogType="website"
        schemaData={[categorySchema, breadcrumbSchema]}
      />
      <Navbar />
      <main className="pt-16 min-h-screen bg-background-50">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="px-4 md:px-6 py-8 md:py-12">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-700">{category.name}</span>
            </nav>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-primary-500 flex items-center justify-center">
                <i className={`${category.icon} text-xl md:text-2xl text-white`}></i>
              </div>
              <div>
                <h1 className="font-heading text-xl md:text-3xl font-bold text-foreground-950 mb-1">
                  {category.name}
                </h1>
                <p className="text-foreground-500 text-sm">
                  {category.description} · 共
                  <strong className="text-foreground-700">{category.articleCount}</strong>
                  篇文章
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 md:px-6 py-8 md:py-12">
          {categoryArticles.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-500 hover:text-primary-600 font-medium mt-4"
              >
                <i className="ri-arrow-left-line"></i>
                返回首页
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {displayArticles.map((article, idx) => (
                  <ArticleCard key={article.id} article={article} isFirst={idx === 0 && currentPage === 1} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage <= 1}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium transition-colors ${
                        p === currentPage
                          ? "bg-primary-500 text-white"
                          : "border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-500"
                      }`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage >= totalPages}
                    className="w-9 h-9 rounded-lg flex items-center justify-center text-sm border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}

          {categoryArticles.length > 0 && (
            <div className="mt-14 md:mt-20">
              <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-6">
                更多分类
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                {categories
                  .filter((c) => c.slug !== slug)
                  .map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.slug}`}
                      className="group flex flex-col items-center gap-2 p-4 rounded-lg border border-background-200/70 bg-background-50 hover:border-primary-300 hover:bg-background-100 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className={`${c.icon} text-primary-500 text-lg`}></i>
                      </div>
                      <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                        {c.name}
                      </span>
                    </Link>
                  ))}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}

function ArticleCard({ article, isFirst }: { article: Article; isFirst: boolean }) {
  return (
    <article
      className={`group rounded-lg overflow-hidden transition-colors ${
        isFirst
          ? "md:col-span-2 md:row-span-2 bg-foreground-950"
          : "bg-background-50 border border-background-200/70 hover:bg-background-100"
      }`}
    >
      <Link to={`/article/${article.id}`} className="block">
        <div className={`relative overflow-hidden ${isFirst ? "h-48 md:h-64 lg:h-80" : "h-44"}`}>
          <img
            src={article.image}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 object-top"
            loading="lazy"
          />
          {isFirst && (
            <div className="absolute top-3 left-3">
              <span className="px-2 py-1 rounded-full bg-primary-500 text-white text-xs font-medium">
                热门推荐
              </span>
            </div>
          )}
        </div>
        <div className={`p-4 ${isFirst ? "lg:p-6" : ""}`}>
          <div className={`flex items-center gap-2 mb-2 text-xs ${isFirst ? "text-foreground-400" : "text-foreground-400"}`}>
            <span className={isFirst ? "text-primary-400" : "text-primary-500"}>{article.categoryName}</span>
            <span>·</span>
            <span>{article.date}</span>
            <span>·</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {(article.views / 10000).toFixed(1)}万
            </span>
          </div>
          <h3
            className={`font-heading font-bold mb-2 line-clamp-2 transition-colors ${
              isFirst
                ? "text-xl md:text-2xl text-white group-hover:text-primary-300"
                : "text-base text-foreground-950 group-hover:text-primary-500"
            }`}
          >
            {article.title}
          </h3>
          <p className={`text-xs leading-relaxed line-clamp-2 ${isFirst ? "text-foreground-400" : "text-foreground-500"}`}>
            {article.excerpt}
          </p>
          {!isFirst && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </Link>
    </article>
  );
}